import React from 'react';
import { Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { ProgramChecklistItem } from '../../../utils/api/endpoints/checklists/checklistApi';

interface ChecklistTestCasesPageProps {
  items: ProgramChecklistItem[];
  programName: string;
  orgName?: string;
}

// ─── Status color configuration ──────────────────────────────────────────────

type StatusConfig = {
  label: string;
  color: string;
};

const STATUS_MAP: Record<string, StatusConfig> = {
  COMPLETED:      { label: 'Passed',         color: '#10b981' },
  FAILED:         { label: 'Failed',         color: '#dc2626' },
  NOT_APPLICABLE: { label: 'NA',             color: '#64748b' },
  NOT_STARTED:    { label: 'Not Started',    color: '#64748b' },
  IN_PROGRESS:    { label: 'In Progress',    color: '#2563eb' },
  PENDING_REVIEW: { label: 'Pending Review', color: '#d97706' },
  REJECTED:       { label: 'Rejected',       color: '#ea580c' },
};

const getStatusConfig = (status: string): StatusConfig =>
  STATUS_MAP[status] ?? { label: status, color: '#64748b' };

// Build a clean paragraph combining description, objective, evidence & references
const buildDetailParagraph = (item: ProgramChecklistItem): string => {
  const parts: string[] = [];
  if (item.description) parts.push(`Description: ${item.description}`);
  if (item.test_objective) parts.push(`Objective: ${item.test_objective}`);
  if (item.expected_evidence) parts.push(`Expected Evidence: ${item.expected_evidence}`);
  if (item.references) parts.push(`References: ${item.references}`);
  return parts.join('\n');
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: '20mm 18mm 18mm 18mm',
    fontFamily: 'Helvetica',
  },

  // Page header – fixed at top of every page
  headerContainer: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontFamily: 'Helvetica-Bold',
    color: '#1e293b',
    marginBottom: 3,
  },
  subHeading: {
    fontSize: 9,
    color: '#64748b',
    fontFamily: 'Helvetica',
    marginBottom: 8,
  },
  dividerLine: {
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
    width: '100%',
  },

  // Test case item
  itemWrapper: {
    flexDirection: 'column',
    marginBottom: 14,
  },
  itemTitleRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 3,
  },
  itemNumber: {
    width: 22,
    fontSize: 10.5,
    fontFamily: 'Helvetica-Bold',
    color: '#0f172a',
  },
  itemTitle: {
    flex: 1,
    fontSize: 10.5,
    fontFamily: 'Helvetica-Bold',
    color: '#0f172a',
    lineHeight: 1.3,
    wordBreak: 'break-all',
  },
  itemDetail: {
    fontSize: 9.5,
    color: '#334155',
    lineHeight: 1.45,
    fontFamily: 'Helvetica',
    paddingLeft: 22,
    marginBottom: 3,
    wordBreak: 'break-all',
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 22,
  },
  statusPrefix: {
    fontSize: 9.5,
    fontFamily: 'Helvetica-Bold',
    color: '#0f172a',
  },
  statusVal: {
    fontSize: 9.5,
    fontFamily: 'Helvetica-Bold',
  },
});

// Helper to insert zero-width spaces into extremely long words so react-pdf can wrap them
const insertZeroWidthSpaces = (text: string, limit = 20): string => {
  if (!text) return '';
  return text.split('\n').map(line => {
    return line.split(' ').map(word => {
      // If the word is a URL, break only at natural delimiters (/, ., -, _) to avoid breaking word characters
      if (word.startsWith('http://') || word.startsWith('https://') || word.startsWith('www.')) {
        const withDelimiters = word.replace(/([\/_.-])/g, '$1\u200b');
        return withDelimiters.split('\u200b').map(chunk => {
          if (chunk.length <= limit) return chunk;
          const subChunks = [];
          for (let i = 0; i < chunk.length; i += limit) {
            subChunks.push(chunk.slice(i, i + limit));
          }
          return subChunks.join('\u200b');
        }).join('\u200b');
      }
      if (word.length <= limit) return word;
      const chunks = [];
      for (let i = 0; i < word.length; i += limit) {
        chunks.push(word.slice(i, i + limit));
      }
      return chunks.join('\u200b');
    }).join(' ');
  }).join('\n');
};

// ─── Component ────────────────────────────────────────────────────────────────

const ChecklistTestCasesPage: React.FC<ChecklistTestCasesPageProps> = ({
  items,
  programName,
  orgName,
}) => {
  const displaySub = orgName ? `${orgName} — ${programName}` : programName;

  return (
    <Page size="A4" style={styles.page} wrap>
      {/* Section header – fixed at top of every page */}
      <View style={styles.headerContainer} fixed>
        <Text style={styles.sectionTitle}>Test Cases</Text>
        <Text style={styles.subHeading}>{displaySub}</Text>
        <View style={styles.dividerLine} />
      </View>

      {/* Test case list */}
      {items.map((item, idx) => {
        const sc = getStatusConfig(item.status);
        const detailText = buildDetailParagraph(item);
        const itemNumber = idx + 1;

        return (
          <View key={item.program_item_id || idx} style={styles.itemWrapper} wrap={false}>
            {/* Title Line: Number + Title */}
            <View style={styles.itemTitleRow}>
              <Text style={styles.itemNumber}>{itemNumber}.</Text>
              <Text style={styles.itemTitle}>
                {insertZeroWidthSpaces(item.title)}
              </Text>
            </View>

            {/* Combined Detail Text (Description, Objective, References) */}
            {detailText ? (
              <Text style={styles.itemDetail}>{insertZeroWidthSpaces(detailText)}</Text>
            ) : null}

            {/* Status Line */}
            <View style={styles.statusRow}>
              <Text style={styles.statusPrefix}>Status: </Text>
              <Text style={[styles.statusVal, { color: sc.color }]}>
                {sc.label}
              </Text>
            </View>
          </View>
        );
      })}
    </Page>
  );
};

export default ChecklistTestCasesPage;
